import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ShieldAlert, RotateCcw, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';
import { useTranslation } from 'react-i18next';

const SEVERITY_STYLES = {
  Critical: 'bg-red-600 text-white',
  Major: 'bg-orange-500 text-white',
  Minor: 'bg-yellow-500 text-black',
};

const CONFIDENCE_STYLES = {
  high: 'bg-slate-800 text-white',
  medium: 'bg-slate-500 text-white',
  low: 'bg-slate-300 text-slate-800',
};

/**
 * Reusable Policy Violations panel for Safety Audit + Incident detail views.
 * Props:
 *   - recordKind: 'audit' | 'incident'
 *   - recordId: string
 *   - initialViolations: array (from the parent record document, optional)
 *   - summary: string (parent's policyViolationsSummary, optional)
 *   - scannedAt: iso string (optional)
 *   - canManage: boolean (admin/supervisor may dismiss; default true)
 *   - onChanged: optional callback after dismiss/rescan
 */
const PolicyViolations = ({
  recordKind,
  recordId,
  initialViolations = [],
  summary = '',
  scannedAt = '',
  canManage = true,
  onChanged,
}) => {
  const { t, i18n } = useTranslation();
  const [violations, setViolations] = useState(initialViolations || []);
  const [showDismissed, setShowDismissed] = useState(false);
  const [rescanning, setRescanning] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [scanInfo, setScanInfo] = useState({ summary, scannedAt });

  useEffect(() => {
    setViolations(initialViolations || []);
    setScanInfo({ summary, scannedAt });
  }, [recordId, initialViolations, summary, scannedAt]);

  const refresh = async () => {
    try {
      const endpoint =
        recordKind === 'audit'
          ? `/safety-audits/${recordId}/policy-violations`
          : `/incident-reports/${recordId}/policy-violations`;
      const { data } = await axiosInstance.get(endpoint);
      setViolations(data.violations || []);
    } catch (e) {
      // silent; component is non-blocking
    }
  };

  const rescan = async () => {
    setRescanning(true);
    try {
      const { data } = await axiosInstance.post(
        `/policy-violations/scan/${recordKind}/${recordId}`
      );
      setViolations(data.violations || []);
      setScanInfo({ summary: data.summary || '', scannedAt: data.scannedAt || '' });
      if (data.scanned === false) {
        toast.info(
          i18n.language === 'es'
            ? 'No hay políticas o manual disponibles para escanear.'
            : 'No policies or manual available to scan.'
        );
      } else {
        toast.success(
          i18n.language === 'es'
            ? `Escaneo completo: ${(data.violations || []).length} hallazgo(s)`
            : `Scan complete: ${(data.violations || []).length} finding(s)`
        );
      }
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(
        i18n.language === 'es'
          ? 'Error al escanear violaciones de política'
          : 'Failed to scan policy violations'
      );
    } finally {
      setRescanning(false);
    }
  };

  const dismiss = async (v) => {
    setBusyId(v.id);
    try {
      await axiosInstance.post(`/policy-violations/${v.id}/dismiss`, {
        reason: '',
      });
      setViolations((prev) =>
        prev.map((x) => (x.id === v.id ? { ...x, dismissed: true } : x))
      );
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(
        i18n.language === 'es' ? 'Error al descartar' : 'Failed to dismiss'
      );
    } finally {
      setBusyId(null);
    }
  };

  const restore = async (v) => {
    setBusyId(v.id);
    try {
      await axiosInstance.post(`/policy-violations/${v.id}/restore`);
      setViolations((prev) =>
        prev.map((x) => (x.id === v.id ? { ...x, dismissed: false } : x))
      );
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(
        i18n.language === 'es' ? 'Error al restaurar' : 'Failed to restore'
      );
    } finally {
      setBusyId(null);
    }
  };

  const visible = violations.filter((v) => (showDismissed ? true : !v.dismissed));
  const openCount = violations.filter((v) => !v.dismissed).length;

  return (
    <Card className="border-red-200 bg-red-50" data-testid={`policy-violations-${recordKind}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-red-600" />
            <h3 className="font-semibold text-lg">
              {i18n.language === 'es'
                ? 'Violaciones de Política Detectadas'
                : 'Policy Violations Detected'}
            </h3>
            <Badge className="bg-red-600 text-white" data-testid="policy-violations-open-count">
              {openCount}
            </Badge>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowDismissed((s) => !s)}
              data-testid="policy-violations-toggle-dismissed"
            >
              {showDismissed ? (
                <EyeOff className="w-4 h-4 mr-1" />
              ) : (
                <Eye className="w-4 h-4 mr-1" />
              )}
              {showDismissed
                ? i18n.language === 'es' ? 'Ocultar descartadas' : 'Hide dismissed'
                : i18n.language === 'es' ? 'Ver descartadas' : 'Show dismissed'}
            </Button>
            {canManage && (
              <Button
                size="sm"
                variant="outline"
                onClick={rescan}
                disabled={rescanning}
                data-testid="policy-violations-rescan-btn"
              >
                <RotateCcw className={`w-4 h-4 mr-1 ${rescanning ? 'animate-spin' : ''}`} />
                {rescanning
                  ? i18n.language === 'es' ? 'Escaneando…' : 'Scanning…'
                  : i18n.language === 'es' ? 'Reescanear' : 'Rescan'}
              </Button>
            )}
          </div>
        </div>

        {scanInfo.summary && (
          <p className="text-sm text-gray-700 mt-3 italic">{scanInfo.summary}</p>
        )}

        {visible.length === 0 ? (
          <p className="text-sm text-gray-600 mt-4">
            {i18n.language === 'es'
              ? 'No se detectaron violaciones de política. La IA comparó este registro con el manual de seguridad de la división y las políticas de la empresa.'
              : 'No policy violations detected. AI compared this record against your division safety manual and company policies.'}
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            {visible.map((v) => (
              <div
                key={v.id}
                className={`border rounded-lg p-3 ${
                  v.dismissed ? 'bg-gray-100 opacity-70' : 'bg-white'
                }`}
                data-testid={`policy-violation-row-${v.id}`}
              >
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={SEVERITY_STYLES[v.severity] || SEVERITY_STYLES.Minor}>
                      {v.severity}
                    </Badge>
                    {v.confidence && (
                      <Badge className={CONFIDENCE_STYLES[v.confidence] || ''}>
                        {i18n.language === 'es' ? 'confianza' : 'confidence'}: {v.confidence}
                      </Badge>
                    )}
                    <span className="font-medium text-sm">{v.source}</span>
                  </div>
                  {canManage && (
                    v.dismissed ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busyId === v.id}
                        onClick={() => restore(v)}
                        data-testid={`policy-violation-restore-${v.id}`}
                      >
                        {i18n.language === 'es' ? 'Restaurar' : 'Restore'}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busyId === v.id}
                        onClick={() => dismiss(v)}
                        data-testid={`policy-violation-dismiss-${v.id}`}
                      >
                        {i18n.language === 'es' ? 'Descartar' : 'Dismiss'}
                      </Button>
                    )
                  )}
                </div>
                {v.quote && (
                  <p className="text-xs text-gray-600 mt-2">
                    <span className="font-semibold">
                      {i18n.language === 'es' ? 'Cita: ' : 'Quote: '}
                    </span>
                    <span className="italic">&ldquo;{v.quote}&rdquo;</span>
                  </p>
                )}
                {v.description && (
                  <p className="text-sm text-gray-800 mt-2">
                    <span className="font-semibold">
                      {i18n.language === 'es' ? 'Problema: ' : 'Issue: '}
                    </span>
                    {v.description}
                  </p>
                )}
                {v.correctiveAction && (
                  <p className="text-sm text-gray-800 mt-2">
                    <span className="font-semibold">
                      {i18n.language === 'es' ? 'Acción correctiva: ' : 'Corrective action: '}
                    </span>
                    {v.correctiveAction}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PolicyViolations;
